/* ---------------------------------------------------------------------------
   What a trace on a photograph comes to.

   The layer between src/data/photoScale.ts, which knows geometry and nothing
   else, and src/screens/measureDialog.ts, which knows the DOM. This knows which
   of a category's measurements a photograph can supply and how to turn a set of
   marks into one of them.

   IT LIVES HERE RATHER THAN IN THE DIALOG FOR TWO REASONS. The store has to
   write the result into the defect draft, and the dialog has to draw it — a
   function in the dialog imported by the store is an import cycle, and the one
   that would break is whichever of the two loaded second. And these are the
   rules worth checking: that a width is refused on an image that cannot resolve
   it, that an area needs three points, that changing what is being measured does
   not silently reinterpret the marks already made. None of that needs a browser,
   and the repository's own habit is that a rule which cannot run in CI should be
   moved until it can.

   The input is structural rather than the store's `MeasureState`, so nothing
   here depends on the store either.
   --------------------------------------------------------------------------- */

import { fieldsFor, type QuantityField } from './quantities.ts';
import {
  CRITICAL_WIDTH_MM,
  mm2ToM2,
  mmToM,
  resolutionVerdict,
  tracedAreaMm2,
  tracedLengthMm,
  type PhotoPoint,
  type PhotoScale,
} from './photoScale.ts';

/** Everything a trace needs, and nothing about how it was collected. */
export interface TraceInput {
  width: number;
  height: number;
  code: string;
  scalePoints: readonly PhotoPoint[];
  /** As typed, because it comes from a number input and "" has to mean unset. */
  referenceMm: string;
  target: string | null;
  points: readonly PhotoPoint[];
}

/**
 * Which of a category's measurements this tool can take off a photograph.
 *
 * Lengths, areas and widths. Not depths — a photograph has no depth information
 * at all, and a spall depth traced on one would be a number with nothing behind
 * it. Not counts, not section loss, not volumes: a volume needs a depth, and
 * section loss needs to see behind the concrete.
 *
 * This is the list that keeps the assist honest about what it is. Everything it
 * offers is a distance in the image plane, because that is the only thing in the
 * image plane.
 */
export function traceableFields(code: string): QuantityField[] {
  return fieldsFor(code).filter(
    (f) => f.key === 'lengthM' || f.key === 'areaM2' || f.key === 'widthMm',
  );
}

/** The scale as the geometry wants it, or null before it is usable. */
export function scaleOf(m: TraceInput): PhotoScale | null {
  if (m.scalePoints.length < 2) return null;
  const lengthMm = Number(m.referenceMm.trim());
  if (!Number.isFinite(lengthMm) || lengthMm <= 0) return null;
  return {
    a: m.scalePoints[0]!,
    b: m.scalePoints[1]!,
    lengthMm,
    width: m.width,
    height: m.height,
  };
}

export interface TraceResult {
  /** In the field's own unit — metres, square metres or millimetres. */
  value: number;
  field: QuantityField;
}

/**
 * The measurement the current trace comes to, or null.
 *
 * Null wherever it cannot honestly be produced, and the caller reports which of
 * the reasons applies. The one that matters is the last: a WIDTH is refused
 * outright on a photograph that cannot resolve 0.3 mm, because that is the
 * threshold Appendix D separates a Good crack from a Fair one at, and a width
 * the image cannot resolve would produce a condition rating out of an invented
 * number. A length or an area off the same photograph is fine and is returned.
 */
export function traceResult(m: TraceInput): TraceResult | null {
  const scale = scaleOf(m);
  if (!scale || !m.target) return null;

  const field = traceableFields(m.code).find((f) => f.key === m.target);
  if (!field) return null;

  if (field.key === 'lengthM') {
    const mm = tracedLengthMm(m.points, scale);
    return mm === null ? null : { value: mmToM(mm), field };
  }

  if (field.key === 'areaM2') {
    const mm2 = tracedAreaMm2(m.points, scale);
    return mm2 === null ? null : { value: mm2ToM2(mm2), field };
  }

  /* A width is a span between two sides, not a path, so only the first two
     marks count. Then the refusal. */
  const mm = tracedLengthMm(m.points.slice(0, 2), scale);
  if (mm === null) return null;

  const verdict = resolutionVerdict(scale, CRITICAL_WIDTH_MM);
  if (!verdict || !verdict.ok) return null;

  return { value: mm, field };
}

/** Why there is no result, in the order the reasons should be reported. */
export function traceRefusal(m: TraceInput): string {
  const scale = scaleOf(m);
  if (!scale) return 'The scale is not set. Go back and mark a reference of known size.';
  if (!m.target) return 'Choose what to measure.';

  if (m.target === 'areaM2') {
    return m.points.length < 3
      ? 'An area needs at least three points.'
      : 'That outline does not enclose an area.';
  }

  if (m.target === 'widthMm') {
    const verdict = resolutionVerdict(scale, CRITICAL_WIDTH_MM);
    if (verdict && !verdict.ok) {
      return `This photograph cannot resolve a crack width. ${verdict.reason}`;
    }
    return 'Tap the two sides of the crack at its widest point.';
  }

  return 'Tap at least two points along the issue.';
}
